import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs/internal/Subject';
import { finalize, takeUntil } from 'rxjs/operators';
import { AdminSettingsService } from 'src/app/admin/servicies/admin-settings.service';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { Constants } from 'src/app/shared/constants';
import { PaymentGateway } from 'src/app/shared/models/request/settings/payment-gateway';
import { SettingsPluginRequestModel } from 'src/app/shared/models/request/settings/settings-plugin-request-model';
import { Result, ResultOfT } from 'src/app/shared/models/response/result';
import { SettingsPluginResponseModel } from 'src/app/shared/models/response/settings/settings-plugin-response-model';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.sass'],
  standalone: false
})
export class PaymentsComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  loading = false;
  loggedInUserId: number;
  private unsubscriber = new Subject<boolean>();
  private progress = new Subject<boolean>();
  settingsPluginResponseModel: SettingsPluginResponseModel;
  credentialsValidated: boolean = false;
  services: any[] = [{ id: 'WorldPay', name: 'WorldPay' }];

  constructor(private fb: FormBuilder, messageService: MessageService, private settingsService: AdminSettingsService, private webStorageService: WebStorageService) {
    super(messageService);
    this.initializeForm();
  }

  ngOnInit(): void {
    const loggedInUser = this.webStorageService.getCurrentUserToken();
    this.loggedInUserId = loggedInUser.userId;
    this.loadPaymentConfig();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      service: ['', Validators.required],
      accountToken: ['', Validators.required],
      accountId: ['', Validators.required],
      acceptorId: ['', Validators.required],
      terminalId: ['', Validators.required],
      transactionUrl: ['', [Validators.required, this.urlValidator]],
      reportingUrl: ['', [Validators.required, this.urlValidator]],
      servicesUrl: ['', [Validators.required, this.urlValidator]],
      hostedPaymentUrl: ['', [Validators.required, this.urlValidator]],
      returnUrl: ['', [Validators.required, this.urlValidator]]
    });
  }

  private urlValidator(control: any) {
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (control.value && !urlPattern.test(control.value)) {
      return { invalidUrl: true };
    }
    return null;
  }

  private loadPaymentConfig(): void {
    this.loading = true;
    this.settingsService
      .getPluginConfiguration(Constants.paymentGateway)
      .pipe(
        takeUntil(this.unsubscriber),
        finalize(() => (this.loading = false))
      )
      .subscribe(
        (result: ResultOfT<any>) => {
          this.processResult<any>(result, () => {
            this.settingsPluginResponseModel = result.responseModel;
            let data = JSON.parse(this.settingsPluginResponseModel.appPluginSettingsValue);
            this.setFormValues(data);
          });
        },
        (httpResponseError) => {
          this.showErrorMessage(httpResponseError.message);
        }
      );
  }

  private setFormValues(config: PaymentGateway): void {
    this.form.patchValue({
      service: config.service,
      accountToken: config.accountToken,
      accountId: config.accountId,
      acceptorId: config.acceptorId,
      terminalId: config.terminalId,
      transactionUrl: config.transactionUrl,
      reportingUrl: config.reportingUrl,
      servicesUrl: config.servicesUrl,
      hostedPaymentUrl: config.hostedPaymentUrl,
      returnUrl: config.returnUrl
    });
  }

  public validateCredentials(data: PaymentGateway): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (this.form.invalid) {
        this.markFormAsTouched();
        this.showErrorMessage('Please fill all required fields before validation');
        resolve(false);
        return;
      }

      this.loading = true;
      this.credentialsValidated = false;

      this.settingsService
        .healthCheck(data)
        .pipe(
          takeUntil(this.unsubscriber),
          finalize(() => (this.loading = false))
        )
        .subscribe(
          (result: ResultOfT<boolean>) => {
            this.processResult<boolean>(result, () => {
              this.credentialsValidated = result.responseModel;
              if (this.credentialsValidated) {
                this.showSuccessMessage('Payment gateway credentials validated successfully');
              } else {
                this.showErrorMessage('Payment gateway credential validation failed');
              }
            });
            resolve(this.credentialsValidated);
          },
          (httpResponseError) => {
            this.credentialsValidated = false;
            this.showErrorMessage(httpResponseError.message);
            resolve(false);
          }
        );
    });
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.markFormAsTouched();
      return;
    }

    const applicationPluginRequestModel = this.getValuesFromForm();
    const validateObj: PaymentGateway = JSON.parse(applicationPluginRequestModel.appPluginSettingsValue);

    const isValid = await this.validateCredentials(validateObj);

    if (!isValid || !this.credentialsValidated) {
      return;
    }

    await this.updatePluginConfiguration(applicationPluginRequestModel);
  }

  private updatePluginConfiguration(applicationPluginRequestModel: SettingsPluginRequestModel): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loading = true;

      this.settingsService
        .updatePluginConfiguration(applicationPluginRequestModel)
        .pipe(
          takeUntil(this.unsubscriber),
          finalize(() => (this.loading = false))
        )
        .subscribe(
          (result: Result) => {
            if (result && result.isSuccess) {
              this.showSuccessMessage('Plugin Payment Gateway settings is updated successfully');
              resolve();
            } else {
              this.showErrorMessage(result.message);
              reject(new Error(result.message));
            }
          },
          (httpResponseError) => {
            this.showErrorMessage(httpResponseError.message);
            reject(httpResponseError);
          }
        );
    });
  }
  private getValuesFromForm(): SettingsPluginRequestModel {
    const formData: PaymentGateway = this.form.value;
    let settingPluginRequestModel = new SettingsPluginRequestModel();
    settingPluginRequestModel = { ...this.settingsPluginResponseModel, appPluginId: this.settingsPluginResponseModel.appPluginId };
    settingPluginRequestModel.appPluginSettingsValue = JSON.stringify(formData);
    settingPluginRequestModel.modifiedByUserInfoId = this.loggedInUserId;
    return settingPluginRequestModel;
  }

  getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (control.errors['invalidUrl']) {
        return `Please enter a valid URL`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      service: 'Service',
      accountToken: 'Account Token',
      accountId: 'Account ID',
      acceptorId: 'Acceptor ID',
      terminalId: 'Terminal ID',
      transactionUrl: 'Transaction URL',
      reportingUrl: 'Reporting URL',
      servicesUrl: 'Services URL',
      hostedPaymentUrl: 'Hosted Payment URL',
      returnUrl: 'Return URL'
    };
    return labels[fieldName] || fieldName;
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control?.invalid && control.touched);
  }

  ngOnDestroy(): void {
    if (this.unsubscriber) {
      this.unsubscriber.next();
      this.unsubscriber.complete();
    }
    if (this.progress) {
      this.progress.complete();
    }
  }
}
